import React from "react";
import { Box, Typography, Tooltip, Chip } from "@mui/material";
import { StyledTableCell, StyledTableRow } from "./styles";
import dayjs from "dayjs"; // If needed for date formatting

const OrderRow = React.memo(
  ({ row, index, startIndex, handleRowClick, roles }) => {
    // Where the order originated. Aggregator OMSes (Pipe17) set `source_channel`
    // to the upstream system ("shopify", "NetSuite", "sps-DillardsDropship");
    // direct providers (Shopify) leave it unset because oms_provider IS the
    // source, so fall back to that rather than showing an empty cell.
    const orderSource = row.source_channel || row.oms_provider || "";
    // `source_channel_type` is Pipe17's own classifier ("online" | "edi" |
    // "wholesale"). Surface it on hover only — it is a signal, not the routing
    // decision (that is `sub_type`, shown in its own column).
    const sourceTooltip = [orderSource, row.source_channel_type]
      .filter(Boolean)
      .join(" · ");

    // Order tags as stored on the order (Pipe17 sends an array, Shopify a
    // comma-separated string; both are normalized to a deduped string[]
    // upstream). Guard the shape anyway — older orders predate the field.
    const tags = Array.isArray(row.tags) ? row.tags.filter(Boolean) : [];
    const MAX_VISIBLE_TAGS = 2;
    const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
    const hiddenTagCount = tags.length - visibleTags.length;
    const tagsTooltip = (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
        {tags.map((tag) => (
          <span key={tag} style={{ fontSize: 11, fontFamily: "poppins" }}>
            {tag}
          </span>
        ))}
      </Box>
    );

    return (
      <StyledTableRow
        sx={{ cursor: "pointer" }}
        onClick={() => handleRowClick(row)}
        key={row.order_id + " - order-list - " + index}
      >
          <StyledTableCell>
            <Typography
              fontSize="12px"
              color="var(--text-secondary)"
              fontWeight={400}
              fontFamily={"poppins"}
              textAlign={"start"}
            >
              {startIndex + index + 1}
            </Typography>
          </StyledTableCell>
          <StyledTableCell>
            <Typography
              fontSize="12px"
              color={row.status === "On Hold" ? "#ba090c" : "#667085"}
              fontWeight={400}
              fontFamily={"poppins"}
            >
              {row.order_id}
            </Typography>
          </StyledTableCell>
          <StyledTableCell>
            <Typography
              fontSize="12px"
              color={row.status === "On Hold" ? "#ba090c" : "#667085"}
              fontWeight={400}
              fontFamily={"poppins"}
            >
              {row.sub_type}
            </Typography>
          </StyledTableCell>
          <StyledTableCell>
            <Tooltip
              title={sourceTooltip}
              arrow
              disableHoverListener={!sourceTooltip}
            >
              <Typography
                fontSize="12px"
                color="var(--text-secondary)"
                fontWeight={400}
                fontFamily={"poppins"}
                sx={{
                  maxWidth: 130,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {orderSource}
              </Typography>
            </Tooltip>
          </StyledTableCell>

          {roles?.find((x) => x.name === "Admin") && (
            <StyledTableCell>
              <Typography
                fontSize="12px"
                color={row.status === "On Hold" ? "#ba090c" : "#667085"}
                fontWeight={400}
                fontFamily={"poppins"}
              >
                {row.client_id?.business_name || ""}
              </Typography>
            </StyledTableCell>
          )}
          {row.type === "M2O" ? (
            <StyledTableCell>
              <Tooltip title={row.asn_no || ""} arrow>
                <Typography
                  fontSize="12px"
                  color={row.status === "On Hold" ? "#ba090c" : "#667085"}
                  fontWeight={400}
                  fontFamily={"poppins"}
                >
                  <div
                    style={{
                      marginTop: 2,
                      marginLeft: 0,
                      maxWidth: 100,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Typography
                      fontSize="12px"
                      color="var(--text-secondary)"
                      fontWeight={400}
                      fontFamily={"poppins"}
                    >
                      {`${row.asn_no || ""}`}
                    </Typography>
                  </div>
                </Typography>
              </Tooltip>
            </StyledTableCell>
          ) : (
            <></>
          )}
          {/* <StyledTableCell>
            <Typography
              fontSize="12px"
              color="var(--text-secondary)"
              fontWeight={400}
              fontFamily={"poppins"}
            >
              {row.customer?.name ||
                `${row?.customer?.first_name || ""} ${
                  row?.customer?.last_name || ""
                }`}
            </Typography>
          </StyledTableCell> */}
          {/* Order tags. Only the first few fit the column, so the rest
              collapse into a "+N" chip and the tooltip carries the full list —
              tags are free text from the storefront and a row that grew with
              the tag count would break the grid. */}
          <StyledTableCell>
            {tags.length > 0 ? (
              <Tooltip title={tagsTooltip} arrow placement="top">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    maxWidth: 180,
                    overflow: "hidden",
                  }}
                >
                  {visibleTags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 20,
                        maxWidth: 80,
                        borderColor: "var(--border-color)",
                        "& .MuiChip-label": {
                          fontSize: 11,
                          fontFamily: "poppins",
                          color: "var(--text-secondary)",
                          paddingInline: "6px",
                        },
                      }}
                    />
                  ))}
                  {hiddenTagCount > 0 && (
                    <Typography
                      fontSize="11px"
                      color="var(--text-secondary)"
                      fontWeight={500}
                      fontFamily={"poppins"}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {`+${hiddenTagCount}`}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            ) : (
              ""
            )}
          </StyledTableCell>
          <StyledTableCell>
            <Typography
              fontSize="12px"
              color="var(--text-secondary)"
              fontWeight={400}
              fontFamily={"poppins"}
            >
              {`${
                row.shipments?.length ? row.shipments[0]?.service || "" : ""
              }`}
            </Typography>
          </StyledTableCell>
          <StyledTableCell>
            <Typography
              fontSize="12px"
              color="var(--text-secondary)"
              fontWeight={400}
              fontFamily={"poppins"}
            >
              {/* Prefer the warehouse's own name: every Pipe17 location shares
                  the `rl` WMS provider, so provider.name alone would render
                  "rl" for all of them. `warehouse` is set only when the whole
                  order ships from one location — on a split fulfilment it is
                  unset and the per-shipment attribution is not populated on
                  the list query, so the cell stays blank and the detail modal
                  names the locations. */}
              {row.warehouse?.name || row.warehouse?.provider?.name || ""}
            </Typography>
          </StyledTableCell>
          <StyledTableCell>
            <Typography
              fontSize="12px"
              color="var(--text-secondary)"
              fontWeight={400}
              fontFamily={"poppins"}
            >
              {`${row.shipping_address?.province || ""}`}
            </Typography>
          </StyledTableCell>
          <StyledTableCell>
            <Typography
              fontSize="12px"
              color="var(--text-secondary)"
              fontWeight={400}
              fontFamily={"poppins"}
            >
              {dayjs(row.order_date).format("MM-DD-YY HH:mm")}
            </Typography>
          </StyledTableCell>
          {roles?.find((x) => x.name === "Admin") && (
            <StyledTableCell>
              {row.shipments?.length
                ? Array.from(new Set(row.shipments.map((x) => x.shipment))).map(
                    (x) => (
                      <Typography
                        fontSize="12px"
                        color="var(--text-secondary)"
                        fontWeight={400}
                        fontFamily={"poppins"}
                      >
                        {x}
                      </Typography>
                    ),
                  )
                : ""}
              {/* {(() => {
                const uniqueShipments = Array.from(
                  new Set(row?.shipments?.map((x) => x.shipment)),
                );
                const visible = uniqueShipments.slice(0, 2).join(", ");
                const tooltip = uniqueShipments.join(", ");
                const extra = uniqueShipments.length > 2 ? ", ..." : "";

                return (
                  <Tooltip title={tooltip}>
                    <Typography
                      fontSize="12px"
                      color="var(--text-secondary)"
                      fontWeight={400}
                      fontFamily="poppins"
                      sx={{
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {visible + extra}
                    </Typography>
                  </Tooltip>
                );
              })()} */}
            </StyledTableCell>
          )}
          {/* <StyledTableCell>
            {row?.shipments?.map((x) => (
              <Typography
                fontSize="12px"
                color="var(--text-secondary)"
                fontWeight={400}
                fontFamily={"poppins"}
              >
                {x.shipment_date
                  ? dayjs(x.shipment_date).format("MM-DD-YY HH:mm")
                  : ""}
              </Typography>
            ))}
          </StyledTableCell> */}
          <StyledTableCell>
            {row.status ? (
              <Typography
                fontSize="12px"
                fontWeight={400}
                fontFamily={"poppins"}
              >
                <span
                  style={{
                    padding: "5px 10px",
                    display: "inline-block",
                    backgroundColor:
                      row.status === "Order Confirmed"
                        ? "var(--bg-highlight)"
                        : row.status === "Shipped"
                          ? "var(--row-bg-even)"
                          : row.status === "Cancelled"
                            ? "var(--bg-error-highlight)"
                            : "",
                    color:
                      row.status === "Order Confirmed"
                        ? "#0070FF"
                        : row.status === "Shipped"
                          ? "#037847"
                          : row.status === "Cancelled"
                            ? "#D13438"
                            : "var(--text-tertiary)",
                    borderRadius: "14px",
                  }}
                >
                  {row.status}
                </span>
              </Typography>
            ) : null}
          </StyledTableCell>
      </StyledTableRow>
    );
  },
);

OrderRow.displayName = "OrderRow";

export default OrderRow;
